import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    MagnifyingGlassIcon,
    ChartBarIcon,
    StarIcon,
    BuildingStorefrontIcon,
    ArrowTrendingUpIcon,
    CalendarIcon,
    EyeIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { authorizedFetch } from '../utils/api';

// Logger utility
const logger = {
    info: (message, data) => {
        console.log(`[INFO] ${message}`, data || '');
    },
    error: (message, error) => {
        console.error(`[ERROR] ${message}`, error);
        if (error?.response) {
            console.error('Response:', {
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data
            });
        }
    },
    warn: (message, data) => {
        console.warn(`[WARN] ${message}`, data || '');
    }
};

// Brand Card Component
function BrandCard({ brand, onViewDetails }) {
    return (
        <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
            {/* Brand Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-xl font-bold mb-1">{brand.name}</h3>
                        <p className="text-blue-100 text-sm">
                            {brand.designer || 'Unknown Designer'}
                        </p>
                    </div>
                    {brand.designer_image ? (
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-white border-2 border-white">
                            <img
                                src={brand.designer_image}
                                alt={`${brand.designer} portrait`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '/logo.svg'; // Default company logo
                                }}
                            />
                        </div>
                    ) : (
                        <BuildingStorefrontIcon className="w-8 h-8 text-blue-200" />
                    )}
                </div>
            </div>

            {/* Brand Content */}
            <div className="p-6">
                {/* Designer Image */}
                <div className="mb-4 flex justify-center">
                    <img
                        src={brand.designer_url || 'https://res.cloudinary.com/djba6ta1n/image/upload/v1753526178/placeholder.png'}
                        alt={`${brand.designer} collection`}
                        className="w-full h-48 object-cover rounded-lg"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/logo.svg'; // Default company logo
                        }}
                    />
                </div>

                {/* Action Button */}
                <button
                    onClick={() => onViewDetails(brand)}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                >
                    <EyeIcon className="w-4 h-4" />
                    View Details
                </button>
            </div>
        </div>
    );
}

// Brand Details Modal Component
function BrandDetailsModal({ brand, isOpen, onClose }) {
    const [brandDetails, setBrandDetails] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && brand) {
            fetchBrandDetails();
        }
    }, [isOpen, brand]);

    const fetchBrandDetails = async () => {
        setLoading(true);
        try {
            const baseUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'http://localhost:8001';
            const url = `${baseUrl}/brands/${encodeURIComponent(brand.name)}/`;
            logger.info(`Fetching brand details from: ${url}`);

            const response = await authorizedFetch(url);
            logger.info(`Brand details response status: ${response.status} ${response.statusText}`);

            if (response.ok) {
                const data = await response.json();
                logger.info(`Brand details received for ${brand.name}`);
                setBrandDetails(data);
            } else {
                // Try to get error details
                try {
                    const errorData = await response.json();
                    logger.error(`Error response data for brand details:`, errorData);
                } catch (e) {
                    logger.error(`Could not parse brand details error response:`, e);
                }
                throw new Error(`API returned ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            logger.error(`Error fetching brand details for ${brand.name}:`, error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            {brandDetails && brandDetails.designer_image ? (
                                <div className="w-16 h-16 rounded-full overflow-hidden bg-white border-2 border-white mr-4">
                                    <img
                                        src={brandDetails.designer_image}
                                        alt={`${brandDetails.designer} portrait`}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '/logo.svg'; // Default company logo
                                        }}
                                    />
                                </div>
                            ) : (
                                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mr-4">
                                    <BuildingStorefrontIcon className="w-10 h-10 text-white" />
                                </div>
                            )}
                            <div>
                                <h2 className="text-2xl font-bold">{brand.name}</h2>
                                <p className="text-blue-100">Designer: {brand.designer || 'Unknown'}</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white hover:text-gray-200 text-2xl font-bold"
                        >
                            ×
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        </div>
                    ) : brandDetails ? (
                        <div className="space-y-6">
                            {/* Designer Image */}
                            <div className="mb-6">
                                <img
                                    src={brandDetails.designer_url || 'https://res.cloudinary.com/djba6ta1n/image/upload/v1753526178/placeholder.png'}
                                    alt={`${brandDetails.designer} collection`}
                                    className="w-full h-64 object-cover rounded-lg"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/logo.svg'; // Default company logo
                                    }}
                                />
                            </div>

                            {/* Brand Info */}
                            <div className="bg-gray-50 p-6 rounded-lg">
                                <h3 className="text-lg font-semibold mb-4">Brand Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <div className="text-sm text-gray-600">Brand Name</div>
                                        <div className="text-lg font-medium">{brandDetails.name}</div>
                                    </div>
                                    <div>
                                        <div className="text-sm text-gray-600">Designer</div>
                                        <div className="text-lg font-medium">{brandDetails.designer || 'Unknown'}</div>
                                    </div>
                                </div>

                                {/* Brand Introduction */}
                                {brandDetails.brand_intro && (
                                    <div className="mt-6">
                                        <h4 className="text-md font-semibold mb-2">About the Brand</h4>
                                        <p className="text-gray-700 whitespace-pre-line">{brandDetails.brand_intro}</p>
                                    </div>
                                )}

                                {/* Designer Introduction */}
                                {brandDetails.designer_intro && (
                                    <div className="mt-6">
                                        <h4 className="text-md font-semibold mb-2">About the Designer</h4>
                                        <p className="text-gray-700 whitespace-pre-line">{brandDetails.designer_intro}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-gray-600">Failed to load brand details</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Main Brands Component
export default function Brands() {
    const [brands, setBrands] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const itemsPerPage = 12;

    useEffect(() => {
        fetchBrands();
        fetchSummary();
    }, [currentPage, sortBy, sortOrder, searchTerm]);

    const fetchBrands = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: itemsPerPage.toString(),
                sort_by: sortBy,
                sort_order: sortOrder,
                ...(searchTerm && { search: searchTerm })
            });

            const baseUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'http://localhost:8001';
            const url = `${baseUrl}/brands/?${params}`;
            logger.info(`Fetching brands from: ${url}`);

            const response = await authorizedFetch(url);
            logger.info(`Response status: ${response.status} ${response.statusText}`);

            if (response.ok) {
                const data = await response.json();
                logger.info(`Brands data received:`, {
                    brandsCount: data.brands?.length,
                    pagination: data.pagination
                });
                setBrands(data.brands);
                setTotalPages(data.pagination.pages);
            } else {
                // Try to get error details
                try {
                    const errorData = await response.json();
                    logger.error(`Error response data:`, errorData);
                } catch (e) {
                    logger.error(`Could not parse error response:`, e);
                }
                throw new Error(`API returned ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            logger.error('Error fetching brands:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchSummary = async () => {
        try {
            const baseUrl = process.env.REACT_APP_BACKEND2_BASE_URL || 'http://localhost:8001';
            const url = `${baseUrl}/brands/summary/`;
            logger.info(`Fetching brands summary from: ${url}`);

            const response = await authorizedFetch(url);
            logger.info(`Summary response status: ${response.status} ${response.statusText}`);

            if (response.ok) {
                const data = await response.json();
                logger.info(`Summary data received:`, data);
                setSummary(data);
            } else {
                // Try to get error details
                try {
                    const errorData = await response.json();
                    logger.error(`Error response data for summary:`, errorData);
                } catch (e) {
                    logger.error(`Could not parse summary error response:`, e);
                }
                throw new Error(`API returned ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            logger.error('Error fetching summary:', error);
        }
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleSortChange = (newSortBy) => {
        if (sortBy === newSortBy) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(newSortBy);
            setSortOrder('desc');
        }
        setCurrentPage(1);
    };

    const handleViewDetails = (brand) => {
        setSelectedBrand(brand);
        setIsModalOpen(true);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loading && brands.length === 0) {
        return (
            <div className="animate-pulse">
                <div className="h-8 bg-gray-300 rounded mb-6"></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {[...Array(12)].map((_, i) => (
                        <div key={i} className="bg-white rounded-xl shadow-lg overflow-hidden">
                            <div className="h-24 bg-gray-300"></div>
                            <div className="p-6">
                                <div className="h-4 bg-gray-300 rounded mb-2"></div>
                                <div className="h-3 bg-gray-200 rounded mb-4"></div>
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div className="h-8 bg-gray-200 rounded"></div>
                                    <div className="h-8 bg-gray-200 rounded"></div>
                                </div>
                                <div className="h-8 bg-gray-300 rounded"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Stats */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-6 rounded-xl shadow-lg">
                        <div className="flex items-center">
                            <BuildingStorefrontIcon className="w-8 h-8 text-blue-600 mr-3" />
                            <div>
                                <div className="text-2xl font-bold text-gray-900">{summary.total_brands}</div>
                                <div className="text-sm text-gray-600">Total Brands</div>
                            </div>
                        </div>
                    </div>
                    {summary.top_brands && summary.top_brands.length > 0 && (
                        <div className="bg-white p-6 rounded-xl shadow-lg">
                            <div className="flex items-center mb-3">
                                <StarIcon className="w-6 h-6 text-yellow-500 mr-2" />
                                <h3 className="text-lg font-semibold">Top Brands</h3>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {summary.top_brands.slice(0, 5).map((brand, index) => (
                                    <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                                        {brand}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Search and Filters */}
            <div className="bg-white p-6 rounded-xl shadow-lg">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    {/* Search */}
                    <div className="relative flex-1 max-w-md">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search brands..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Sort Options */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => handleSortChange('name')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${sortBy === 'name'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            Name {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </button>
                        <button
                            onClick={() => handleSortChange('designer')}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${sortBy === 'designer'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            Designer {sortBy === 'designer' && (sortOrder === 'asc' ? '↑' : '↓')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Brands Grid */}
            {brands.length === 0 ? (
                <div className="text-center py-12">
                    <BuildingStorefrontIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No brands found</h3>
                    <p className="text-gray-600">Try adjusting your search criteria</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {brands.map((brand) => (
                        <BrandCard
                            key={brand.name}
                            brand={brand}
                            onViewDetails={handleViewDetails}
                        />
                    ))}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center space-x-2">
                    <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded-lg font-medium ${currentPage === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                            }`}
                    >
                        Previous
                    </button>

                    {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                        const page = index + 1;
                        return (
                            <button
                                key={page}
                                onClick={() => handlePageChange(page)}
                                className={`px-4 py-2 rounded-lg font-medium ${currentPage === page
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                                    }`}
                            >
                                {page}
                            </button>
                        );
                    })}

                    <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded-lg font-medium ${currentPage === totalPages
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                            }`}
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Brand Details Modal */}
            <BrandDetailsModal
                brand={selectedBrand}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
            />
        </div>
    );
}